import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { rootPath } from '@/common/utils/pathPrefix.util';
import { merge } from 'lodash-es';
import { AppMode } from '@/common/utils/getAppMode';
import initSelfService from '@/self-service/initSelfService';
import { initFederatedApp } from '@/self-service/initFederatedApp';
import type { AxiosHeaderValue, AxiosRequestConfig } from 'axios';
import type { useAuthStore } from '@/common/store/pinia/auth';
import type { usePermissionsStore, AbilityRule } from '@/common/store/pinia/permissions';

export type RouterMode = 'history' | 'abstract';

export interface ModePreset {
  router: {
    mode: RouterMode;
    permissionRedirect: boolean;
  };
  pageAfterLogin: string;
  featuresUrl: string;
  featuresConfig: AxiosRequestConfig;
  features: {
    s3: boolean;
  };
  layout: {
    header: boolean;
    sidebar: boolean;
  };
  theme: {
    injectStyles: boolean;
  };
  bootstrap: {
    waitForRouterReady: boolean;
  };
  abilityRules: AbilityRule[];
  initSelfService: ({
    authStore,
    permissionsStore,
    providers,
    setAppModeSettings,
  }: {
    authStore: ReturnType<typeof useAuthStore>,
    permissionsStore: ReturnType<typeof usePermissionsStore>,
    providers?: AppOptions['providers']
    setAppModeSettings: (settings: DeepPartial<ModePreset>) => void;
  }) => Promise<void>;
  websocket: {
    wsUrl: string;
    vncWebSocketUrl: string;
    queryParams: Record<string, string | undefined>;
    headers: Record<string, AxiosHeaderValue | undefined>;
  };
}

const presets: Record<AppMode, ModePreset> = {
  default: {
    router: {
      mode: 'history',
      permissionRedirect: true,
    },
    pageAfterLogin: 'monitoring',
    featuresUrl: '/features.json',
    featuresConfig: {},
    features: {
      s3: true,
    },
    layout: {
      header: true,
      sidebar: true,
    },
    theme: {
      injectStyles: true,
    },
    bootstrap: {
      waitForRouterReady: true,
    },
    abilityRules: [],
    initSelfService,
    websocket: {
      wsUrl: `${rootPath}/ws`,
      vncWebSocketUrl: '/websockify',
      queryParams: {},
      headers: {},
    }
  },
  federated: {
    router: {
      mode: 'abstract',
      permissionRedirect: false,
    },
    pageAfterLogin: 'computeServerInstances',
    featuresUrl: `${process.env.PUBLIC_ASSET_PREFIX || ''}/features.json`,
    featuresConfig: {
      baseURL: process.env.NODE_ENV === 'development' ? undefined : '/',
    },
    features: {
      s3: false,
    },
    layout: {
      header: false,
      sidebar: false,
    },
    theme: {
      injectStyles: false,
    },
    bootstrap: {
      waitForRouterReady: false,
    },
    abilityRules: [
      {
        action: 'read',
        subject: 'compute_image_file',
        inverted: true
      },
      {
        action: 'create',
        subject: 'compute_images',
        inverted: true
      }
    ],
    initSelfService: initFederatedApp,
    websocket: {
      wsUrl: '',
      vncWebSocketUrl: '',
      queryParams: {},
      headers: {},
    }
  },
};

const useAppModeStore = defineStore('appMode', () => {
  const mode = ref<AppMode>('default');

  const preset = computed<ModePreset>(() => presets[mode.value]);
  const pageAfterLogin = computed(() => preset.value.pageAfterLogin);
  const isFederated = computed(() => mode.value === 'federated');

  const isFeatureEnabled = (feature: keyof ModePreset['features']) => preset.value.features[feature];
  const isVisible = (element: keyof ModePreset['layout']) => preset.value.layout[element];
  const shouldInjectThemeStyles = computed(() => preset.value.theme.injectStyles);
  const shouldWaitForRouterReady = computed(() => preset.value.bootstrap.waitForRouterReady);
  const shouldUsePermissionRedirect = computed(() => preset.value.router.permissionRedirect);
  const configure = (appMode: AppMode) => {
    mode.value = appMode;
  };
  const setSettings = (settings: DeepPartial<ModePreset>) => {
    presets[mode.value] = merge({}, presets[mode.value], settings);
  };

  return {
    mode,
    preset,
    configure,
    pageAfterLogin,
    isFeatureEnabled,
    isVisible,
    shouldInjectThemeStyles,
    shouldWaitForRouterReady,
    shouldUsePermissionRedirect,
    isFederated,
    setSettings
  };
});

export default useAppModeStore;
